import React, { useEffect, useState, useCallback, useMemo } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import iconFileDelete from "../../../assets/media/icon-file-delete.svg";
import iconSearch from "../../../assets/media/icon-search.svg";
import Navbar from "../../navbar";
import Header from "../../header";
import { useDispatch, useSelector } from "react-redux";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import {
  addEmployee,
  deleteEmployee,
  resendEmail,
  reactivateEmployee,
} from "../../../store/reducers/employeeSlice.js";
import { fetchEmployee } from "../../../store/reducers/employeeSlice.js";
import { updateUser } from "../../../store/reducers/userSlice.js";
import Pagination from "../dashboard/appPagination";
import { createNotification } from "../../../store/reducers/notificationSlice.js";
import userImagePlaceholder from "../../../assets/media/avatar2.jpg";
import Modal from "../../modal/Modal.js";
import PhoneInput from "react-phone-input-2";
import DatePicker from "react-datepicker";
import NotFound from "../../common/notFound.jsx";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";



const AppEmployees = () => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const pagination = useSelector((state) => state.employee.pagination);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth.user);
  const employee = useSelector((state) => state.employee);
  const createLoading = useSelector((state) => state.employee.createLoading);
  const fetchLoading = useSelector((state) => state.employee.fetchLoading);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredemployee, setFilteredEmplyee] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("id");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);


  // debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);
  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
  };

  const deleteModalOpenHandler = () => {
    setDeleteModalOpen(!deleteModalOpen);
  };

  const editModalOpenHandler = () => {
    setEditModalOpen(!editModalOpen);
  };
  const users = employee?.users;
  const authToken = user.access_token;

  const initialValues = {
    full_name: "",
    national_id_number: "",
    email: "",
    phone: "",
  };

  const initialEditValues = {
    full_name: "",
    national_id_number: "",
    email: "",
    phone: "",
  };

  const validationSchema = Yup.object({
    full_name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .required("Full Name is required"),
    national_id_number: Yup.string().required("National Id Number is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phone: Yup.string().required("Phone Number is required"),
  });

  const editValidationSchema = Yup.object({
    full_name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .required("Full Name is required"),
    national_id_number: Yup.string().required("National Id Number is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phone: Yup.string().required("Phone Number is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { resetForm }) => {
      try {
        // Try to add new employee
        const result = await dispatch(
          addEmployee({
            userData: {
              full_name: values.full_name,
              national_id_number: values.national_id_number,
              email: values.email,
              phone: values.phone,
              company_id: user.user.company_id,
              user_role: "employee",
            },
            accessToken: authToken,
          })
        );

        // If employee was added successfully, reset form and refetch employees
        if (addEmployee.fulfilled.match(result)) {
          resetForm();

          // Refetch employees to update the list
          let queryParams = { is_deleted: false };
          if (user.user?.user_role === "company_admin" && user.user?.company_id) {
            queryParams.company_id = user.user.company_id;
          } else if (user.user?.user_role === "employee" && user.user?.company_id) {
            queryParams.company_id = user.user.company_id;
          }

          if (startDate) {
            queryParams.start_date = moment(startDate).format("YYYY-MM-DD");
          }
          if (endDate) {
            queryParams.end_date = moment(endDate).format("YYYY-MM-DD");
          }

          if (debouncedSearchTerm) {
            queryParams[searchBy] = debouncedSearchTerm;
          }

          queryParams.page = currentPage;
          queryParams.limit = itemsPerPage;

          dispatch(
            fetchEmployee({ accessToken: authToken, queryParams: queryParams })
          );

          dispatch(
            createNotification({
              user_id: user.user.id,
              status: "employee_added",
              accessToken: authToken,
              is_admin: true,
              companyName: user.user?.company_name,
              employeeName: values.full_name
            })
          );
        } else if (addEmployee.rejected.match(result)) {
          // Check if error is about user already existing (soft deleted)
          const errorMessage = (result.error?.message || "").toLowerCase();
          const errorResponse = result.error?.response?.data?.message || "";
          const fullErrorMessage = (errorMessage + " " + errorResponse.toLowerCase()).toLowerCase();

          if (
            fullErrorMessage.includes("already registered") ||
            fullErrorMessage.includes("already exists") ||
            fullErrorMessage.includes("mobile number") ||
            fullErrorMessage.includes("email") ||
            fullErrorMessage.includes("phone") ||
            fullErrorMessage.includes("duplicate") ||
            fullErrorMessage.includes("taken")
          ) {
            // Try to reactivate the user instead
            try {
              const reactivateResult = await dispatch(
                reactivateEmployee({
                  email: values.email,
                  company_id: user.user.company_id,
                  accessToken: authToken,
                })
              );

              // If reactivation was successful, update user info and refetch employees
              if (reactivateEmployee.fulfilled.match(reactivateResult)) {
                // Get the reactivated user ID from the response
                const reactivatedUser = reactivateResult.payload?.user || reactivateResult.payload;
                const userId = reactivatedUser?.id || reactivateResult.payload?.id;

                // Update the user information after reactivation
                if (userId) {
                  try {
                    await dispatch(
                      updateUser({
                        userId: userId,
                        userData: {
                          full_name: values.full_name,
                          national_id_number: values.national_id_number,
                          email: values.email,
                          phone: values.phone,
                          accessToken: authToken,
                        },
                      })
                    );
                  } catch (updateError) {
                    console.error("Error updating reactivated employee:", updateError);
                    // If update fails, try to find user after refetch
                    // Refetch employees first to get the updated list
                    let queryParams = { is_deleted: false };
                    if (user.user?.user_role === "company_admin" && user.user?.company_id) {
                      queryParams.company_id = user.user.company_id;
                    } else if (user.user?.user_role === "employee" && user.user?.company_id) {
                      queryParams.company_id = user.user.company_id;
                    }

                    if (startDate) {
                      queryParams.start_date = moment(startDate).format("YYYY-MM-DD");
                    }
                    if (endDate) {
                      queryParams.end_date = moment(endDate).format("YYYY-MM-DD");
                    }

                    if (debouncedSearchTerm) {
                      queryParams[searchBy] = debouncedSearchTerm;
                    }

                    const fetchResult = await dispatch(
                      fetchEmployee({ accessToken: authToken, queryParams: queryParams })
                    );

                    // Try to find and update the user after refetch
                    if (fetchResult.payload) {
                      const foundUser = fetchResult.payload.find(
                        (u) => u.email?.toLowerCase() === values.email?.toLowerCase()
                      );
                      if (foundUser?.id) {
                        await dispatch(
                          updateUser({
                            userId: foundUser.id,
                            userData: {
                              full_name: values.full_name,
                              national_id_number: values.national_id_number,
                              email: values.email,
                              phone: values.phone,
                              accessToken: authToken,
                            },
                          })
                        );
                      }
                    }
                  }
                } else {
                  // If we don't have userId from response, refetch and find user
                  let queryParams = { is_deleted: false };
                  if (user.user?.user_role === "company_admin" && user.user?.company_id) {
                    queryParams.company_id = user.user.company_id;
                  } else if (user.user?.user_role === "employee" && user.user?.company_id) {
                    queryParams.company_id = user.user.company_id;
                  }

                  if (startDate) {
                    queryParams.start_date = moment(startDate).format("YYYY-MM-DD");
                  }
                  if (endDate) {
                    queryParams.end_date = moment(endDate).format("YYYY-MM-DD");
                  }

                  if (debouncedSearchTerm) {
                    queryParams[searchBy] = debouncedSearchTerm;
                  }

                  queryParams.page = currentPage;
                  queryParams.limit = itemsPerPage;

                  const fetchResult = await dispatch(
                    fetchEmployee({ accessToken: authToken, queryParams: queryParams })
                  );

                  // Find and update the user after refetch
                  if (fetchResult.payload) {
                    const foundUser = fetchResult.payload.find(
                      (u) => u.email?.toLowerCase() === values.email?.toLowerCase()
                    );
                    if (foundUser?.id) {
                      await dispatch(
                        updateUser({
                          userId: foundUser.id,
                          userData: {
                            full_name: values.full_name,
                            national_id_number: values.national_id_number,
                            email: values.email,
                            phone: values.phone,
                            accessToken: authToken,
                          },
                        })
                      );
                    }
                  }
                }

                resetForm();

                // Refetch employees to update the list (final refetch)
                let queryParams = { is_deleted: false };
                if (user.user?.user_role === "company_admin" && user.user?.company_id) {
                  queryParams.company_id = user.user.company_id;
                } else if (user.user?.user_role === "employee" && user.user?.company_id) {
                  queryParams.company_id = user.user.company_id;
                }

                if (startDate) {
                  queryParams.start_date = moment(startDate).format("YYYY-MM-DD");
                }
                if (endDate) {
                  queryParams.end_date = moment(endDate).format("YYYY-MM-DD");
                }

                if (debouncedSearchTerm) {
                  queryParams[searchBy] = debouncedSearchTerm;
                }

                queryParams.page = currentPage;
                queryParams.limit = itemsPerPage;

                dispatch(
                  fetchEmployee({ accessToken: authToken, queryParams: queryParams })
                );

                dispatch(
                  createNotification({
                    user_id: user.user.id,
                    status: "employee_updated",
                    accessToken: authToken,
                    is_admin: true,
                    companyName: user.user?.company_name,
                    employeeName: values.full_name
                  })
                );
              }
            } catch (reactivateError) {
              console.error("Error reactivating employee:", reactivateError);
            }
          }
        }
      } catch (error) {
        console.error("Error adding employee:", error);
      }
    },
  });

  const { values, handleChange, handleSubmit, handleBlur, errors } = formik;

  // Edit form formik instance
  const editFormik = useFormik({
    initialValues: initialEditValues,
    validationSchema: editValidationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      if (!editingUser) return;

      setUpdateLoading(true);
      try {
        const result = await dispatch(
          updateUser({
            userId: editingUser.id,
            userData: {
              full_name: values.full_name,
              national_id_number: values.national_id_number,
              email: values.email,
              phone: values.phone,
              accessToken: authToken,
            },
          })
        );

        if (updateUser.fulfilled.match(result)) {
          resetForm();
          setEditModalOpen(false);
          setEditingUser(null);

          // Refetch employees to update the list
          let queryParams = { is_deleted: false };
          if (user.user?.user_role === "company_admin" && user.user?.company_id) {
            queryParams.company_id = user.user.company_id;
          } else if (user.user?.user_role === "employee" && user.user?.company_id) {
            queryParams.company_id = user.user.company_id;
          }

          if (startDate) {
            queryParams.start_date = moment(startDate).format("YYYY-MM-DD");
          }
          if (endDate) {
            queryParams.end_date = moment(endDate).format("YYYY-MM-DD");
          }

          if (debouncedSearchTerm) {
            queryParams[searchBy] = debouncedSearchTerm;
          }

          queryParams.page = currentPage;
          queryParams.limit = itemsPerPage;

          dispatch(
            fetchEmployee({ accessToken: authToken, queryParams: queryParams })
          );

          dispatch(
            createNotification({
              user_id: user.user.id,
              status: "employee_updated",
              accessToken: authToken,
              is_admin: true,
              companyName: user.user?.company_name,
              employeeName: editValues.full_name
            })
          );
        }
      } catch (error) {
        console.error("Error updating employee:", error);
      } finally {
        setUpdateLoading(false);
      }
    },
  });

  const {
    values: editValues,
    handleChange: handleEditChange,
    handleSubmit: handleEditSubmit,
    handleBlur: handleEditBlur,
    errors: editErrors,
    setValues: setEditValues
  } = editFormik;

  // Handler to open edit modal and populate form
  const handleEditEmployee = (item) => {
    setEditingUser(item);
    setEditValues({
      full_name: item.full_name || "",
      national_id_number: item.national_id_number || "",
      email: item.email || "",
      phone: item.phone || "",
    });
    setEditModalOpen(true);
  };

  // const getTotalPages = () => {
  //   return Math.ceil(filteredemployee.length / itemsPerPage);
  // };

  useEffect(() => {
    let queryParams = { is_deleted: false };

    // SECURITY: Add company_id filter at API level
    if (user.user?.user_role === "company_admin" && user.user?.company_id) {
      queryParams.company_id = user.user.company_id;
    } else if (user.user?.user_role === "employee" && user.user?.company_id) {
      // Employees should only see users from their company
      queryParams.company_id = user.user.company_id;
    }

    if (startDate) {
      queryParams.start_date = moment(startDate).format("YYYY-MM-DD");
    }
    if (endDate) {
      queryParams.end_date = moment(endDate).format("YYYY-MM-DD");
    }

    if (debouncedSearchTerm) {
      queryParams[searchBy] = debouncedSearchTerm;
    }

    queryParams.page = currentPage;
    queryParams.limit = itemsPerPage;

    dispatch(
      fetchEmployee({ accessToken: authToken, queryParams: queryParams })
    );
  }, [authToken, dispatch, startDate, endDate, user.user?.user_role, user.user?.company_id, debouncedSearchTerm, searchBy, currentPage, itemsPerPage]);


  useEffect(() => {
    if (users && users.length > 0) {
      const filtered = users
        .filter((item) => {
          // First apply base filters (role, deletion status, company)
          if (item.user_role === "company_admin") return false;
          // Check for true (boolean) or "true" (string) or 1 (int)
          // if (item.is_deleted === true || item.is_deleted === "true" || item.is_deleted === 1) return false;
          if (item.company_id !== user.user.company_id) return false;

          // If no search term, we are done (passed base filters)
          // if (!searchQuery && !searchTerm) return true;

          return true; // Trust server-side search results
        })
        .sort((a, b) => b.id - a.id);

      setFilteredEmplyee(filtered);
    } else {
      setFilteredEmplyee([]);
    }
    // setCurrentPage(1);  <-- Don't reset page effectively on every render.
  }, [users, searchQuery, searchTerm, itemsPerPage]);

  const deleteuserdata = async () => {
    if (selectedUser) {
      try {
        await dispatch(
          deleteEmployee({
            email: selectedUser.email,
            accessToken: authToken,
          })
        );
        // SECURITY: Add company_id filter at API level
        // SECURITY: Add company_id filter at API level
        let queryParams = { is_deleted: false };
        if (user.user?.user_role === "company_admin" && user.user?.company_id) {
          queryParams.company_id = user.user.company_id;
        } else if (user.user?.user_role === "employee" && user.user?.company_id) {
          queryParams.company_id = user.user.company_id;
        }

        if (startDate) {
          queryParams.start_date = moment(startDate).format("YYYY-MM-DD");
        }
        if (endDate) {
          queryParams.end_date = moment(endDate).format("YYYY-MM-DD");
        }

        if (debouncedSearchTerm) {
          queryParams[searchBy] = debouncedSearchTerm;
        }

        queryParams.page = currentPage;
        queryParams.limit = itemsPerPage;

        dispatch(fetchEmployee({
          accessToken: authToken,
          queryParams: queryParams
        }));
        setDeleteModalOpen(false);
        setSelectedUser(null);
      } catch (error) {
        console.error("Error deleting user:", error);
      }
    }
  };
  const handleResendEmail = useCallback(async (item, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      await dispatch(resendEmail({
        authToken: authToken,
        email: item.email,
        company_id: user.user?.company_id
      }));
      // Optionally refetch employees after resending email
      let queryParams = { is_deleted: false };
      if (user.user?.user_role === "company_admin" && user.user?.company_id) {
        queryParams.company_id = user.user.company_id;
      } else if (user.user?.user_role === "employee" && user.user?.company_id) {
        queryParams.company_id = user.user.company_id;
      }

      if (startDate) {
        queryParams.start_date = moment(startDate).format("YYYY-MM-DD");
      }
      if (endDate) {
        queryParams.end_date = moment(endDate).format("YYYY-MM-DD");
      }

      if (debouncedSearchTerm) {
        queryParams[searchBy] = debouncedSearchTerm;
      }

      queryParams.page = currentPage;
      queryParams.limit = itemsPerPage;

      dispatch(
        fetchEmployee({ accessToken: authToken, queryParams: queryParams })
      );
    } catch (error) {
      console.error("Error resending email:", error);
    }
  }, [dispatch, authToken, user.user?.user_role, user.user?.company_id, startDate, endDate]);






  // inside AppEmployees component
  const columns = React.useMemo(
    () => [
      {
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      },
      {
        header: "Employee Id",
        accessorKey: "id",
        cell: (info) => `GB-000000${info.getValue()}`,
      },
      {
        header: "Name",
        accessorKey: "full_name",
        cell: (info) => {
          const item = info.row.original;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img
                src={
                  item.image && typeof item.image === 'string' && item.image.trim() !== ""
                    ? item.image
                    : userImagePlaceholder
                }
                alt="Avatar"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                }}
              />
              <div>
                <strong>{item.full_name}</strong>
                <span style={{ display: "block" }}>
                  {item.national_id_number || "--"}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        header: "Email",
        accessorKey: "email",
        cell: (info) => (
          <>{info.getValue()}</>
        ),
      },
      {
        header: "Phone",
        accessorKey: "phone",
        cell: (info) => (
          <span >{info.getValue()}</span>
        ),
      },
      {
        header: "Created At",
        accessorKey: "created_at",
        cell: (info) => (
          <span>
            {info.getValue()?.slice(0, 10)}
          </span>
        ),
      },
      {
        header: "Status",
        accessorKey: "active",
        cell: (info) => (
          <span className={` ${info.getValue() === true ? "text-success" : "text-danger"}`}>
            {info.getValue() === true ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => (
          <div className="text-center">Actions</div>
        ),
        cell: (info) => {
          const item = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >

                  {item.status === "inactive" || item.active === false ? (
                    <>
                      <li>
                        <Link
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditEmployee(item);
                          }}
                          className="dropdown-item"
                          to="#"
                        >
                          Edit
                        </Link>
                      </li>
                      <li>
                        <Link
                          onClick={(e) => handleResendEmail(item, e)}
                          className="dropdown-item"
                          to="#"
                        >
                          Resend Email
                        </Link>
                      </li>
                    </>
                  ) : (
                    <>
                      <li>
                        <Link
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (item.user_role === "company_admin") return;
                            setSelectedUser(item);
                            setDeleteModalOpen(true);
                          }}
                          className="dropdown-item"
                          to="#"
                        >
                          Delete
                        </Link>
                      </li>
                    </>
                  )}


                </ul>
              </div>
            </div>

          );
        },
      },
    ],
    [userImagePlaceholder, handleResendEmail, currentPage, itemsPerPage] // dependencies
  );

  // No client-side pagination needed for the table data
  // const paginatedData = React.useMemo(() => {
  //   const startIndex = (currentPage - 1) * itemsPerPage;
  //   return filteredemployee.slice(startIndex, startIndex + itemsPerPage);
  // }, [filteredemployee, currentPage, itemsPerPage]);

  const table = useReactTable({
    data: filteredemployee, // Server returns paginated data
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          <div className="boardHeader noMg">
            <div className="boardHeadLeft">
              <h2>Employees</h2>
            </div>
          </div>
        </div>
        <div className="empAdd">
          <h3 className="noMg">Add Employee</h3>
          <form className="addEmployee-form" onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Full Name</label>
                  <input
                    name="full_name"
                    type="text"
                    placeholder="Marvin McKinney"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.full_name}
                    className={errors.full_name ? "error-input" : ""}
                  />
                  {errors.full_name && (
                    <div className="error-message">
                      {formik.errors.full_name}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>National Insurance Number</label>
                  <input
                    name="national_id_number"
                    type="text"
                    placeholder="12345678"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.national_id_number}
                    className={errors.national_id_number ? "error-input" : ""}
                    maxLength={8}
                  />
                  {errors.national_id_number && (
                    <div className="error-message">
                      {formik.errors.national_id_number}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Email Address</label>
                  <input
                    name="email"
                    type="text"
                    placeholder="email@gamingboard.com"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    className={errors.email ? "error-input" : ""}
                  />
                  {errors.email && (
                    <div className="error-message">{formik.errors.email}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Phone No.</label>
                  <PhoneInput
                    country={"bs"}
                    value={values.phone}
                    onChange={(phone) => formik.setFieldValue("phone", phone)}
                    onBlur={handleBlur}
                    placeholder="(242) 555-0122"
                    countryCodeEditable={false}
                    masks={{ bs: '...-....' }}
                    inputProps={{ name: "phone" }}
                    className={errors.phone ? "error-input" : ""}
                  />
                  {errors.phone && (
                    <div className="error-message">{formik.errors.phone}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap noLabel">
                  <button type="submit" disabled={createLoading}>
                    {createLoading ? (
                      <>
                        <div
                          class="text-xs spinner-border text-light"
                          style={{ width: "1rem", height: "1rem" }}
                          role="status"
                        ></div>
                        <span class="sr-only ml-5"> Loading...</span>
                      </>
                    ) : (
                      "Add Employee"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div className="boxAppBoard boxWhite">
          <div
            style={{ flexWrap: "wrap", gap: "10px" }}
            className="boardHeader"
          >
            <div className="boardHeadLeft">
              <h2>All Employees</h2>
            </div>
            <div
              style={{
                gap: "10px",
                flexWrap: "wrap",
                justifyContent: "flex-start",
              }}
              className="boardHeadRight"
            >
              <select onChange={handleSearchByChange} value={searchBy} >
                <option value="id">Search by Employee ID</option>
                <option value="username">Search by Employee Name</option>
                <option value="email">Search by Employee Email</option>
                <option value="phone">Search by Employee Phone</option>

              </select>
              <div className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  value={searchTerm}
                />
                <button>
                  <img src={iconSearch} alt="" />
                </button>
              </div>
              {/* <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="dd/MM/yyyy"
                />
              </div> */}
            </div>
          </div>
          <div className="boardContent table-responsive">
            <table className="table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={pagination?.totalPages || 0}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteModalOpen}
        handleClose={deleteModalOpenHandler}
        colWidth={4}
      >
        <div className="text-left">
          <h3>Delete Employee</h3>
          <p>Are you sure you want to delete this employee?</p>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button className="btn btn-danger mr-2" onClick={deleteuserdata}>
              Confirm
            </button>
            <button
              className="btn btn-secondary"
              onClick={deleteModalOpenHandler}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Employee Modal */}
      <Modal
        show={editModalOpen}
        handleClose={editModalOpenHandler}
        colWidth={8}
      >
        <div className="text-left">
          <h3>Edit Employee</h3>
          <form className="addEmployee-form" onSubmit={handleEditSubmit}>
            <div className="row empAdd">
              <div className="col-12 col-lg-6">
                <div className="fieldWrap">
                  <label>Full Name</label>
                  <input
                    name="full_name"
                    type="text"
                    placeholder="Marvin McKinney"
                    onChange={handleEditChange}
                    onBlur={handleEditBlur}
                    value={editValues.full_name}
                    className={editErrors.full_name ? "error-input" : ""}
                  />
                  {editErrors.full_name && (
                    <div className="error-message">
                      {editFormik.errors.full_name}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="fieldWrap">
                  <label>National Insurance Number</label>
                  <input
                    name="national_id_number"
                    type="text"
                    placeholder="12345678"
                    onChange={handleEditChange}
                    onBlur={handleEditBlur}
                    value={editValues.national_id_number}
                    className={editErrors.national_id_number ? "error-input" : ""}
                    maxLength={8}
                  />
                  {editErrors.national_id_number && (
                    <div className="error-message">
                      {editFormik.errors.national_id_number}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="fieldWrap">
                  <label>Email Address</label>
                  <input
                    name="email"
                    type="text"
                    placeholder="email@gamingboard.com"
                    onChange={handleEditChange}
                    onBlur={handleEditBlur}
                    value={editValues.email}
                    className={editErrors.email ? "error-input" : ""}
                  />
                  {editErrors.email && (
                    <div className="error-message">{editFormik.errors.email}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="fieldWrap">
                  <label>Phone No.</label>
                  <PhoneInput
                    country={"bs"}
                    value={editValues.phone}
                    onChange={(phone) => editFormik.setFieldValue("phone", phone)}
                    onBlur={handleEditBlur}
                    placeholder="(242) 555-0122"
                    countryCodeEditable={false}
                    masks={{ bs: '...-....' }}
                    inputProps={{ name: "phone" }}
                    className={editErrors.phone ? "error-input" : ""}
                  />
                  {editErrors.phone && (
                    <div className="error-message">{editFormik.errors.phone}</div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={editModalOpenHandler}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={updateLoading}
                  >
                    {updateLoading ? (
                      <>
                        <div
                          className="text-xs spinner-border text-light"
                          style={{ width: "1rem", height: "1rem" }}
                          role="status"
                        ></div>
                        <span className="sr-only ml-5"> Loading...</span>
                      </>
                    ) : (
                      "Update Employee"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default AppEmployees;
